// TEMPLATES-T2 (docs/design/2026-10-28-agent-templates.md §1, §5, §6): what a role template
// version carries besides its skills, standing orders and models — the defaults a deploy from the
// role starts from. Stored on `cp.agent_template_version` (one column each, snake_case JSON as
// here), part of the version's content hash once validated, and frozen with it.
//
//   required_connections  families the DE needs connected before it leaves onboarding
//   access_defaults       default access per family (R/A/D/M, per-action levels) and the seat's
//                         agent policy classes (direct / ask / never)
//   kpis                  metric keys with targets (role or DE catalog keys)
//   cadence               routines seeded as seat routines (RRULE) when a board is named
//   model_tier            light · standard · heavy (decision 2; models behind them per company)
//   budget_default_minor  the seat's default monthly budget, in the company's minor unit
//   probation             real tasks and days at Draft level, whichever ends later (decision 3)
//
// The seat's reporting defaults are Agent Reach's (`reporting_defaults`, REACH R1); they are not
// repeated here.
import { z } from "zod";
import {
  accessDefaultSchema,
  CONNECTED_FAMILIES,
  SKILL_ACCESS_FAMILIES,
  SKILL_PACK_KEY_RE,
} from "../skill-packs/manifest";

export const MODEL_TIERS = ["light", "standard", "heavy"] as const;
export type ModelTier = (typeof MODEL_TIERS)[number];

/** Agent policy modes a template may name for a class (agent-policy/classes.ts). */
export const POLICY_MODES = ["direct", "ask", "never"] as const;
export type PolicyModeName = (typeof POLICY_MODES)[number];
export const policyRank = (m: PolicyModeName): number => (m === "direct" ? 2 : m === "ask" ? 1 : 0);

const classKey = z.string().regex(/^[a-z][a-z_]{0,63}$/);
const metricKey = z
  .string()
  .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/)
  .max(64);

export const requiredConnectionSchema = z.strictObject({
  family: z.enum(CONNECTED_FAMILIES as unknown as [string, ...string[]]),
  /** One line the access step shows ("reads and drafts your mail"). */
  why: z.string().min(1).max(200),
});

export const accessDefaultsSchema = z.strictObject({
  families: z.partialRecord(z.enum(SKILL_ACCESS_FAMILIES), accessDefaultSchema),
  classes: z.record(classKey, z.enum(POLICY_MODES)),
});
export type AccessDefaults = z.infer<typeof accessDefaultsSchema>;

export const roleKpiSchema = z.strictObject({
  key: metricKey,
  /** The weekly target; null: the client sets it (e.g. qualified leads per weekday). */
  target: z.number().min(0).max(1_000_000_000).nullable(),
  direction: z.enum(["gte", "lte"]),
});
export type RoleKpi = z.infer<typeof roleKpiSchema>;

export const cadenceEntrySchema = z.strictObject({
  key: z.string().regex(/^[a-z][a-z0-9_-]{0,40}$/),
  name: z.string().min(1).max(120),
  /** In the company's calendar time zone (`recurring_work.schedule`). */
  rrule: z
    .string()
    .regex(/^(RRULE:)?FREQ=/)
    .max(300),
  /** The pack the routine runs, when one does. */
  skill: z.string().regex(SKILL_PACK_KEY_RE).optional(),
});
export type CadenceEntry = z.infer<typeof cadenceEntrySchema>;

export const probationSchema = z.strictObject({
  tasks: z.int().min(0).max(1000),
  days: z.int().min(0).max(365),
});
export type Probation = z.infer<typeof probationSchema>;

/** Decision 3: 10 real tasks and 7 days at Draft level, whichever ends later. */
export const DEFAULT_PROBATION: Probation = { tasks: 10, days: 7 };

const uniqueBy =
  <T>(key: (t: T) => string, what: string) =>
  (xs: readonly T[], ctx: z.RefinementCtx) => {
    const seen = new Set<string>();
    xs.forEach((x, i) => {
      const k = key(x);
      if (seen.has(k))
        ctx.addIssue({ code: "custom", message: `duplicate ${what} ${k}`, path: [i] });
      seen.add(k);
    });
  };

export const roleDefaultsSchema = z.strictObject({
  required_connections: z
    .array(requiredConnectionSchema)
    .max(12)
    .superRefine(uniqueBy((r) => r.family, "family")),
  access_defaults: accessDefaultsSchema,
  kpis: z
    .array(roleKpiSchema)
    .max(16)
    .superRefine(uniqueBy((k) => k.key, "KPI")),
  cadence: z
    .array(cadenceEntrySchema)
    .max(16)
    .superRefine(uniqueBy((c) => c.key, "routine")),
  model_tier: z.enum(MODEL_TIERS),
  budget_default_minor: z.int().min(0).max(100_000_000),
  probation: probationSchema,
});
export type RoleDefaults = z.infer<typeof roleDefaultsSchema>;

/**
 * Problems a role's defaults have beyond their shape: a family reached through a connection that
 * the role grants access to but does not require, and a required family it grants nothing on.
 */
export function roleDefaultsProblems(d: RoleDefaults): { path: string; message: string }[] {
  const out: { path: string; message: string }[] = [];
  const required = new Set(d.required_connections.map((r) => r.family));
  for (const fam of Object.keys(d.access_defaults.families))
    if ((CONNECTED_FAMILIES as readonly string[]).includes(fam) && !required.has(fam))
      out.push({
        path: `access_defaults.families.${fam}`,
        message: `${fam} is reached through a connection: add it to required_connections`,
      });
  for (const fam of required)
    if (!(fam in d.access_defaults.families))
      out.push({
        path: "required_connections",
        message: `${fam} is required but the role grants no access on it`,
      });
  return out;
}
